import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  ComplaintAssignee,
  ComplaintDetails,
  ComplaintResolution,
  ComplaintListParams,
  ComplaintStatus,
  ComplaintSummary,
  UnreadComplaintMessage,
} from '../types/complaint.types';

/** Complaint calls — read with complaints.manage (complaints.read: the limited view); act with complaints.manage. */

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export async function getComplaints(
  params: ComplaintListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<ComplaintSummary>> {
  const page = await http.get<AdminPage<ComplaintSummary>>('/admin/complaints', { params, signal });
  return {
    items: page.items.map(toComplaintSummary),
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

export async function getComplaint(
  complaintId: number,
  signal?: AbortSignal,
): Promise<ComplaintDetails> {
  const details = await http.get<ComplaintDetails>(`/admin/complaints/${complaintId}`, {
    signal,
  });
  return {
    ...details,
    complaint: toComplaintSummary(details.complaint),
    details: details.details ?? null,
    refundAmount: details.refundAmount ?? null,
    modifiedByName: details.modifiedByName ?? null,
    modifiedAt: details.modifiedAt ?? null,
    comments: details.comments ?? [],
    pendingRefundApprovalId: details.pendingRefundApprovalId ?? null,
  };
}

/**
 * UT system leaves null properties out of its JSON (IgnoreNullValues), so a
 * nullable field can arrive missing. Turned back into null here so `=== null`
 * means what it says — a missing pendingRefundApprovalId once read as "a
 * refund is waiting" on every complaint.
 */
function toComplaintSummary(summary: ComplaintSummary): ComplaintSummary {
  return {
    ...summary,
    orderId: summary.orderId ?? null,
    complaintType: summary.complaintType ?? null,
    authorName: summary.authorName ?? null,
    countryId: summary.countryId ?? null,
    assignedToUserId: summary.assignedToUserId ?? null,
    assigneeName: summary.assigneeName ?? null,
    resolution: summary.resolution ?? null,
    preview: summary.preview ?? null,
    lastMessageAt: summary.lastMessageAt ?? null,
  };
}

export async function addComplaintComment(complaintId: number, text: string): Promise<number> {
  return http.post<number>(`/admin/complaints/${complaintId}/comments`, { text });
}

export async function setComplaintStatus(
  complaintId: number,
  status: ComplaintStatus,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/complaints/${complaintId}/status`, {
    status,
    reason,
  });
  return result.changed;
}

export async function getComplaintAssignees(
  complaintId: number,
  signal?: AbortSignal,
): Promise<ComplaintAssignee[]> {
  return http.get<ComplaintAssignee[]>(`/admin/complaints/${complaintId}/assignees`, { signal });
}

/** Gives the complaint to a staff member, or unassigns it (null). Audited. */
export async function assignComplaint(
  complaintId: number,
  assigneeUserId: number | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/complaints/${complaintId}/assign`, {
    assigneeUserId,
  });
  return result.changed;
}

/**
 * Closes the complaint with an outcome. `refund` asks for a refund of `amount`
 * (needs refunds.request) and returns the approval id — the complaint closes
 * as "resolved · refund" only when someone else approves it. `no_refund`
 * closes it now and returns null.
 */
export async function resolveComplaint(
  complaintId: number,
  input: { outcome: ComplaintResolution; amount: number | null; reason: string },
): Promise<number | null> {
  return http.post<number | null>(`/admin/complaints/${complaintId}/resolve`, input);
}

/** Your Messages: conversations where the customer wrote last and you have not read it. */
export async function getUnreadComplaintMessages(
  countryId: number | undefined,
  signal?: AbortSignal,
): Promise<UnreadComplaintMessage[]> {
  return http.get<UnreadComplaintMessage[]>('/admin/complaints/unread', {
    params: { countryId },
    signal,
  });
}
